import { useEffect, useRef, useState } from 'react';
import { MAP_OPTIONS, type MapPoint, decodePath, fitToPoints, loadMaps } from '../maps';

/** A stable empty layer: a fresh array on every render would redraw the map. */
const NO_POINTS: MapPoint[] = [];

const ACCENT = '#b04065';
const GREY = '#9aa0a6';

interface MapCanvasProps {
  points: MapPoint[];
  /** A second tour drawn underneath in grey — the day as it stands today. */
  base?: MapPoint[];
  /** Draws the ride joining the numbered points, in order. */
  route?: boolean;
  height?: string;
  title?: string;
  className?: string;
}

/**
 * A Google map, or nothing at all. Every failure path — no key, script blocked,
 * offline — renders null so the surrounding screen is never affected.
 */
export function MapCanvas({
  points,
  base = NO_POINTS,
  route = false,
  height = '14rem',
  title,
  className,
}: MapCanvasProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<google.maps.Map | null>(null);
  const drawn = useRef<{ setMap(value: google.maps.Map | null): void }[]>([]);
  const [maps, setMaps] = useState<typeof google.maps | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    void loadMaps().then((loaded) => {
      if (!active) return;
      if (loaded) setMaps(loaded);
      else setFailed(true);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!maps || !container.current) return;

    map.current ??= new maps.Map(container.current, MAP_OPTIONS);
    const target = map.current;

    for (const overlay of drawn.current) overlay.setMap(null);
    drawn.current = [];

    const basePath = drawTour(maps, target, drawn.current, base, route, true);
    const path = drawTour(maps, target, drawn.current, points, route, false);

    fitToPoints(target, maps, [...base, ...points], [...basePath, ...path]);
  }, [maps, points, base, route]);

  if (failed) return null;

  return (
    <div
      ref={container}
      className={className ? `map ${className}` : 'map'}
      style={{ height }}
      role="img"
      aria-label={title ?? 'Carte'}
    />
  );
}

/**
 * One tour: its markers, and the line joining them. A muted tour is the same
 * ride in grey, without its house — both tours leave the same door, so only the
 * top layer draws it.
 */
function drawTour(
  maps: typeof google.maps,
  target: google.maps.Map,
  drawn: { setMap(value: google.maps.Map | null): void }[],
  points: MapPoint[],
  route: boolean,
  muted: boolean,
): (google.maps.LatLng | google.maps.LatLngLiteral)[] {
  let order = 0;
  let housed = false;

  for (const point of points) {
    // One house, whatever the tour: the muted layer borrows it, and the ride home
    // comes back to the very pin the day left from.
    if (point.home && (muted || housed)) continue;
    if (point.home) housed = true;
    const dot = muted || point.muted;
    if (!dot && !point.home) order += 1;
    const label = { text: point.label ?? String(order), color: '#fff', fontSize: '0.8rem' };

    drawn.push(
      new maps.Marker({
        map: target,
        position: { lat: point.lat, lng: point.lng },
        title: point.title ?? '',
        ...(dot
          ? { icon: mutedIcon(maps), zIndex: 1 }
          : point.home
            ? { icon: homeIcon(maps), zIndex: 3 }
            : point.candidate
              ? { icon: candidateIcon(maps), label, zIndex: 4 }
              : { label, zIndex: 2 }),
      }),
    );
  }

  const stops = points.filter((point) => !point.muted);
  const path = route ? routePath(maps, stops) : [];
  if (path.length > 1) {
    drawn.push(
      new maps.Polyline({
        map: target,
        path,
        strokeColor: muted ? GREY : ACCENT,
        strokeOpacity: muted ? 0.6 : 0.8,
        strokeWeight: 3,
        zIndex: muted ? 1 : 2,
      }),
    );
  }

  return path;
}

/**
 * The line joining the stops: the streets she actually rides wherever the leg
 * into a stop came with geometry, a straight segment to it where it did not.
 */
function routePath(
  maps: typeof google.maps,
  stops: MapPoint[],
): (google.maps.LatLng | google.maps.LatLngLiteral)[] {
  const path: (google.maps.LatLng | google.maps.LatLngLiteral)[] = [];

  for (const [index, stop] of stops.entries()) {
    const ride = index === 0 ? null : decodePath(maps, stop.encodedPolyline);
    if (ride) path.push(...ride);
    else path.push({ lat: stop.lat, lng: stop.lng });
  }

  return path;
}

/** « Maison »: a house in the route's own colour, unmistakably not a numbered stop. */
function homeIcon(maps: typeof google.maps): google.maps.Symbol {
  return {
    path: 'M12 2 2 11h3v11h6v-7h2v7h6V11h3z',
    scale: 1.1,
    anchor: new maps.Point(12, 22),
    fillColor: ACCENT,
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 1.5,
  };
}

/** The neighbours: a small grey dot, visibly not one of the day's stops. */
function mutedIcon(maps: typeof google.maps): google.maps.Symbol {
  return {
    path: maps.SymbolPath.CIRCLE,
    scale: 5,
    fillColor: GREY,
    fillOpacity: 0.9,
    strokeColor: '#5f6368',
    strokeWeight: 1,
  };
}

/** The séance being previewed: a numbered stop, ringed so it stands out. */
function candidateIcon(maps: typeof google.maps): google.maps.Symbol {
  return {
    path: maps.SymbolPath.CIRCLE,
    scale: 12,
    fillColor: ACCENT,
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 3,
  };
}
